import { useState, useEffect, useCallback } from 'react';
import {
  DevHubTextField,
  DevHubFormControl,
  DevHubAutocomplete,
} from '@devxdiscover/devhub-ui';
import { gql, useLazyQuery } from '@apollo/client';
import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder';
import { CustomFieldExtensionSchema } from '@backstage/plugin-scaffolder-react';

/**
 * The schema for the {@link ApplicationPicker} return value.
 */
export const ApplicationPickerSchema: CustomFieldExtensionSchema = {
  returnValue: {
    type: 'number',
    title: 'Application ID',
    description: 'The DevHub Application ID',
  },
};

type AppOption = {
  applicationId: number;
  name: string;
  status: string;
};

const SEARCH_APPLICATIONS_QUERY = gql`
  query SearchApplications(
    $filter: ResourceCatalogApplicationFilter
    $pagination: PaginationInput
  ) {
    resources {
      applicationsPaged(filter: $filter, pagination: $pagination) {
        applications {
          applicationId
          name
          status
        }
      }
    }
  }
`;

/**
 * Builds a filter for the applications query based on user input.
 * If the input is a number, searches by applicationIds; otherwise by name wildcard.
 * @param searchText The user input from the autocomplete field
 * @returns A filter object for the GraphQL query
 */
const buildFilter = (searchText: string): Record<string, unknown> => {
  const trimmed = searchText.trim();
  if (!trimmed) return {};

  const asNumber = parseInt(trimmed, 10);
  if (!isNaN(asNumber) && String(asNumber) === trimmed) {
    return { applicationIds: [asNumber] };
  }
  return { applicationName: `*${trimmed}*` };
};

/**
 * A scaffolder field extension that provides an autocomplete picker
 * for DevHub applications. Users can search by application name or ID.
 * @param props The field extension props provided by the scaffolder
 * @returns The selected `applicationId` (number) or `undefined` if none is selected.
 */
export const ApplicationPicker = (
  props: FieldExtensionComponentProps<number | undefined>,
) => {
  const { formData, onChange, rawErrors, idSchema, required } = props;
  const [apps, setApps] = useState<AppOption[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [selectedApp, setSelectedApp] = useState<AppOption | null>(null);

  const [fetchApplications, { data, loading, error }] = useLazyQuery(
    SEARCH_APPLICATIONS_QUERY,
    { errorPolicy: 'all' },
  );

  const doSearch = useCallback(
    (searchText: string) => {
      fetchApplications({
        variables: {
          filter: buildFilter(searchText),
          pagination: { offset: 0, limit: 25 },
        },
      });
    },
    [fetchApplications],
  );

  // Initial load
  useEffect(() => {
    doSearch('');
  }, [doSearch]);

  // Update options when data arrives
  useEffect(() => {
    if (data) {
      const fetched: AppOption[] =
        data?.resources?.applicationsPaged?.applications ?? [];
      const sorted = [...fetched]
        .filter(a => a.status !== 'Retired')
        .sort((a, b) => a.name.localeCompare(b.name));
      setApps(sorted);
    }
  }, [data]);

  // Debounced search on input change
  useEffect(() => {
    const timeout = setTimeout(() => {
      doSearch(inputValue);
    }, 300);
    return () => clearTimeout(timeout);
  }, [inputValue, doSearch]);

  const handleSelect = (_: unknown, value: AppOption | null) => {
    setSelectedApp(value);
    onChange(value?.applicationId);
  };

  const getOptionLabel = (option: AppOption) =>
    `${option.name} (ID: ${option.applicationId})`;

  return (
    <DevHubFormControl
      margin="normal"
      required={required}
      error={rawErrors && rawErrors.length > 0 && !formData}
    >
      {error ? (
        <DevHubTextField
          error
          label="Error"
          defaultValue="Failed to load applications"
          helperText="Could not fetch application list."
          variant="outlined"
        />
      ) : (
        <DevHubAutocomplete
          id={idSchema?.$id}
          value={selectedApp}
          loading={loading}
          loadingText="Searching..."
          options={apps}
          getOptionLabel={getOptionLabel}
          onChange={handleSelect}
          onInputChange={(_, val) => setInputValue(val)}
          autoSelect={false}
          renderInput={params => (
            <DevHubTextField
              {...params}
              label="Application"
              margin="normal"
              helperText="Search by application name or ID"
              variant="outlined"
              required={required}
              InputProps={params.InputProps}
            />
          )}
        />
      )}
    </DevHubFormControl>
  );
};
